import { useContext } from 'react';
import type { RefObject } from 'react';

import { Link } from 'react-router-dom';

import { makeStyles } from '@mui/styles';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';

import Typography from '@classify/react-core-components/Typography';

import { getPathName, ROUTES } from '@openclassrooms/routing';
import { BackLink } from '@openclassrooms/ui-web/ui';
import { STATUSES } from '@openclassrooms/ui-web/learning-activities';
import { translate } from '@openclassrooms/translations';
import CircularLearningProgress from '@classify/react-product-components/CircularLearningProgress';

import CourseContext from '../../contexts/courseContext';

interface Props {
  headerRef: RefObject<HTMLDivElement>;
}

const useStyles = makeStyles(
  ({ spacing, palette, breakpoints, zIndex }) => ({
    root: {
      top: 0,
      boxShadow: `inset 0px -1px 0px ${palette.status.neutral}`,
      zIndex: zIndex.appBar - 1,
      backgroundColor: palette.layout.background.navigation,
    },
    backArrow: {
      marginRight: spacing(2),
      color: palette.text.primary,
    },
    content: {
      display: 'flex',
      minWidth: 0,
    },
    title: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    header: {
      padding: spacing(0, 2),
      [breakpoints.up('sm')]: {
        padding: spacing(0, 4),
      },
      [breakpoints.up('lg')]: {
        padding: spacing(0, 12),
      },
    },
    headerExtended: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      padding: spacing(2),
      minHeight: 66,
      alignItems: 'self-start',
      [breakpoints.up('sm')]: {
        padding: spacing(3, 4),
      },
      [breakpoints.up('lg')]: {
        padding: spacing(3, 12),
      },
    },
    contentExtended: {
      paddingTop: 10,
      flexDirection: 'column',
    },
    subContent: {
      display: 'flex',
      flexDirection: 'column',
      flexWrap: 'wrap',
      marginLeft: '44px',
      '& > *': {
        marginTop: spacing(1),
      },
      [breakpoints.up('sm')]: {
        alignItems: 'center',
        flexDirection: 'row',
      },
    },
    learningProgressContainer: {
      display: 'flex',
      alignItems: 'center',
    },
    statusPassedLabel: {
      color: palette.status.success,
    },
  }),
  { classNamePrefix: 'CourseChapterHeader' }
);

const CourseChapterHeader = ({ headerRef }: Props) => {
  const classes = useStyles();

  const { course, currentUser } = useContext(CourseContext);

  if (!course) return null;

  const { userCourseCompletionRate: progression, learningActivityStatus: status } = course;

  const isExtended = status !== STATUSES.STATUS_NOT_STARTED && currentUser;

  const headerClasses = {
    root: isExtended ? classes.headerExtended : classes.header,
  };

  const shouldShowProgression = status !== STATUSES.STATUS_PASSED;

  const courseLink = getPathName(
    ROUTES.COURSES.DETAIL,
    {
      courseIdSlug: `${course.id}-${course.slug}`,
    },
    false
  );

  return (
    <AppBar className={classes.root} position="relative" color="transparent" ref={headerRef}>
      <Toolbar classes={headerClasses}>
        <BackLink component={Link} to={courseLink} aria-label={translate('global.forms.word.back')}>
          {course.title}
        </BackLink>
        {isExtended && (
          <div className={classes.subContent}>
            {shouldShowProgression && (
              <>
                <div className={classes.learningProgressContainer}>
                  {progression !== 100 && (
                    <CircularLearningProgress
                      sx={{ pr: 1 }}
                      value={progression}
                      maxValue={100}
                      ariaHidden="true"
                    />
                  )}
                  <Typography variant="bodySmall">
                    {translate(
                      'course.chapter.completion_rate',
                      { completionRate: Math.floor(progression) },
                      progression
                    )}
                  </Typography>
                </div>
              </>
            )}
            {status === STATUSES.STATUS_PASSED && (
              <Typography className={classes.statusPassedLabel} variant="subheading">
                {translate('course.chapter.completion_rate', { completionRate: 100 }, 100)}
              </Typography>
            )}
          </div>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default CourseChapterHeader;
